import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  type Unsubscribe
} from "firebase/firestore";
import type { User } from "firebase/auth";
import type { AssetGroup } from "../types/imobi";
import { db } from "./firebase";

function normalizeAssetGroupId(value: string) {
  return value
    .trim()
    .toLocaleLowerCase("pt-BR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function mapAssetGroup(companyId: string, id: string, data: Record<string, unknown>): AssetGroup {
  return {
    id,
    companyId,
    name: String(data.name ?? id),
    memberUserIds: Array.isArray(data.memberUserIds) ? data.memberUserIds.map(String) : [],
    active: data.active !== false,
    createdAt: typeof data.createdAt === "string" ? data.createdAt : "",
    createdBy: typeof data.createdBy === "string" ? data.createdBy : "",
    updatedAt: typeof data.updatedAt === "string" ? data.updatedAt : undefined,
    updatedBy: typeof data.updatedBy === "string" ? data.updatedBy : undefined
  };
}

export function subscribeAssetGroups(companyId: string, onChange: (groups: AssetGroup[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db || !companyId) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "companies", companyId, "assetGroups"), orderBy("name")),
    (snapshot) => onChange(snapshot.docs.map((itemDoc) => mapAssetGroup(companyId, itemDoc.id, itemDoc.data()))),
    (error) => onError(error.message)
  );
}

export async function createAssetGroup(companyId: string, name: string, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  const trimmedName = name.trim().toLocaleUpperCase("pt-BR");
  const groupId = normalizeAssetGroupId(trimmedName);

  if (!companyId || !groupId) {
    return;
  }

  await setDoc(doc(db, "companies", companyId, "assetGroups", groupId), {
    name: trimmedName,
    memberUserIds: [],
    active: true,
    createdAt: serverTimestamp(),
    createdBy: user.uid
  }, { merge: true });
}

export async function updateAssetGroupMembers(companyId: string, groupId: string, memberUserIds: string[], user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  await updateDoc(doc(db, "companies", companyId, "assetGroups", groupId), {
    memberUserIds,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
}

export async function setAssetGroupActive(companyId: string, groupId: string, active: boolean, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  await updateDoc(doc(db, "companies", companyId, "assetGroups", groupId), {
    active,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
}
